import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { AzDocumentUpdateModalComponent } from '../../../../../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';
import { LoggerService } from '../../../../../../core/logging/logger.service';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { SelectedLegalEntityService } from '../../../../../../shared/services/selected-legal-entity.service';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-bncs-assessment',
  imports: [
    FormsModule,
    AzButtonComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
  ],
  templateUrl: './bncs-assessment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bncs-assessment.component.scss',
})
export class BncsAssessmentComponent implements OnInit, OnChanges {
  @Input() legalEntityId: number | null = null;
  @Input() contactCount: number = 0;
  @Input() bncsDocuments: Array<{
    document_file_id: number;
    name: string;
    version: number;
    document_type: string;
    upload_status: string;
    uploaded_by: string;
    comments: string | null;
    created_date: number;
    s3_uri: string | null;
    size: number | null;
  }> = [];
  documentDate: string = '';
  selectedFile: File | null = null;
  isModalOpen: boolean = false;
  isStatusModalOpen: boolean = false;
  isUploading: boolean = false;
  statusModalTitle: string = 'B&CS Assessment';

  @ViewChild(StatusChangeModalComponent) statusModal!: StatusChangeModalComponent;
  @ViewChild(AzDocumentUpdateModalComponent) documentUpdateModal!: AzDocumentUpdateModalComponent;
  documentTypes: string[] = []; // Will be loaded from API
  filteredDocumentTypes: string[] = []; // Filtered based on current status
  documentTypeObjects: Array<{ name: string; type: string }> = []; // Store full objects
  allowedFileTypes: string[] = []; // Unique file types from documentTypeObjects
  documentTypesLoaded: boolean = false; // Track if document types were successfully loaded

  // Computed properties for document type selector
  get shouldDisableDocumentTypeSelector(): boolean {
    return this.filteredDocumentTypes.length === 1;
  }

  get preselectedDocumentType(): string {
    return this.filteredDocumentTypes.length === 1 ? this.filteredDocumentTypes[0] : '';
  }

  constructor(
    private logger: LoggerService,
    private legalEntityService: LegalEntityService,
    private selectedLegalEntity: SelectedLegalEntityService,
    public userPermissions: UserPermissionsService,
  ) {}

  ngOnInit(): void {
    this.loadDocumentTypes();
    this.updateDocumentDate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update document date when bncsDocuments Input changes
    if (changes['bncsDocuments'] && !changes['bncsDocuments'].firstChange) {
      this.updateDocumentDate();
    }
  }

  /**
   * Load B&CS assessment document types from backend
   */
  loadDocumentTypes(): void {
    this.legalEntityService.getBncsAssessmentDocumentTypes().subscribe({
      next: (types) => {
        // Store full objects
        this.documentTypeObjects = types;
        // Transform DocumentType[] to string[] for the selector
        this.documentTypes = types.map((type) => type.name);
        // Filter document types based on current status
        this.filterDocumentTypesByStatus();
        // Extract unique file types
        this.allowedFileTypes = [...new Set(types.map((type) => type.type))];
        // Mark as successfully loaded
        this.documentTypesLoaded = true;
      },
      error: () => {
        // Fallback to empty arrays if API fails
        this.documentTypes = [];
        this.filteredDocumentTypes = [];
        this.allowedFileTypes = [];
        // Keep documentTypesLoaded as false so it can retry on next modal open
        this.documentTypesLoaded = false;
      },
    });
  }

  /**
   * Filter document types based on the current legal entity status
   * Extracts keywords like "Update", "New entry", or "Deactivation" from status
   */
  filterDocumentTypesByStatus(): void {
    const currentStatus = this.selectedLegalEntity.status();

    if (!currentStatus) {
      // If no status, show all document types
      this.filteredDocumentTypes = this.documentTypes;
      return;
    }

    // Extract the keyword from status (before the first '-' or ':')
    // Examples: "Update - Pending B&CS Assessment" -> "Update"
    //          "New entry - Pending B&CS Assessment" -> "New entry"
    //          "Pending: Deactivation - Pending B&CS Assessment" -> "Deactivation"
    let statusKeyword = currentStatus.split('-')[0].trim();

    // Handle "Pending: Deactivation" case - extract "Deactivation"
    if (statusKeyword.includes(':')) {
      statusKeyword = statusKeyword.split(':')[1].trim();
    }

    // Filter document types that include the status keyword
    // E.g., if statusKeyword is "Update", show only "B&CS template - Update"
    this.filteredDocumentTypes = this.documentTypes.filter((docType) =>
      docType.toLowerCase().includes(statusKeyword.toLowerCase()),
    );

    // If no match found, show all document types as fallback
    if (this.filteredDocumentTypes.length === 0) {
      this.filteredDocumentTypes = this.documentTypes;
    }
  }

  /**
   * Update document date from the most recent B&CS assessment document
   */
  updateDocumentDate(): void {
    if (this.bncsDocuments.length > 0) {
      // Sort by created_date descending to get the most recent
      const mostRecent = this.bncsDocuments.reduce((prev, current) =>
        current.created_date > prev.created_date ? current : prev,
      );
      this.documentDate = this.formatDate(mostRecent.created_date);
    } else {
      this.documentDate = '';
    }
  }

  openModal(): void {
    if (this.contactCount === 0) {
      this.statusModalTitle = 'Warning';
      this.isStatusModalOpen = true;
      this.statusModal.showError(
        'In order to upload the B&CS assessment file, please enter at least one contact in the Contact Information section',
        'Invalid Status Change',
        'error',
      );
      return;
    }

    // Retry loading document types if they weren't loaded successfully before
    if (!this.documentTypesLoaded) {
      this.loadDocumentTypes();
    } else {
      // Re-filter document types based on current status
      this.filterDocumentTypesByStatus();
    }
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  closeStatusModal(): void {
    this.isStatusModalOpen = false;

    // If the modal had an error state, clear the file but keep the preselection
    if (this.statusModal?.state === 'error') {
      this.clearFileOnly();
    }
  }

  /**
   * Clear only the selected file, preserving the preselected document type
   */
  private clearFileOnly(): void {
    if (this.documentUpdateModal) {
      this.documentUpdateModal.selectedFile = null;
      this.documentUpdateModal.errorMessage = '';
      // Keep selectedDocumentType (preselection) intact
    }
  }

  handleUpload(data: { file: File | null; documentType: string; comments: string }): void {
    if (!this.legalEntityId) {
      return;
    }

    if (!data.file) {
      return;
    }

    if (!data.documentType) {
      return;
    }

    // Show loading state
    this.isUploading = true;

    // Step 1: Generate presigned upload URL
    const uploadRequest = {
      document_type_name: data.documentType,
      comments: data.comments || undefined,
    };

    this.legalEntityService
      .generateBncsAssessmentUploadUrl(this.legalEntityId, uploadRequest)
      .subscribe({
        next: (uploadResponse) => {
          // Step 2: Upload to S3
          this.legalEntityService.uploadToS3(data.file!, uploadResponse.presigned_post).subscribe({
            next: (event) => {
              if (event.type === HttpEventType.Response) {
                // Step 3: Construct S3 URI and validate
                const url = uploadResponse.presigned_post.url;
                const bucketMatch = url.match(/https:\/\/([^.]+)\.s3\./);
                const bucket = bucketMatch ? bucketMatch[1] : 'bcr-tool-documents-dev';
                const s3Uri = `s3://${bucket}/${uploadResponse.s3_key}`;

                this.legalEntityService
                  .validateBncsAssessment(this.legalEntityId!, s3Uri)
                  .subscribe({
                    next: () => {
                      this.isUploading = false;
                      this.closeModal();
                      this.resetModalForm();

                      // Show success modal
                      this.statusModalTitle = 'B&CS Assessment';
                      this.isStatusModalOpen = true;
                      this.statusModal.showSuccessWithMessage(
                        'Document uploaded and validated successfully!',
                      );

                      // Reload entity detail to update status in toolbar
                      this.selectedLegalEntity.setStatusLoading(true);
                      this.legalEntityService.getLegalEntityDetail(this.legalEntityId!).subscribe({
                        next: (detail) => {
                          this.selectedLegalEntity.setEntityDetail(detail);
                          const status = detail.bcr_status?.status_name || '';
                          const bcrStatusId = detail.bcr_status?.id || null;
                          this.selectedLegalEntity.setStatus(status);
                          this.selectedLegalEntity.setBcrStatusId(bcrStatusId);
                          this.selectedLegalEntity.setStatusLoading(false);
                        },
                        error: (error) => {
                          this.logger.error('Failed to reload entity detail:', error);
                          this.selectedLegalEntity.setStatusLoading(false);
                        },
                      });

                      this.selectedLegalEntity.notifyStatusChangeApproved();
                    },
                    error: (error) => {
                      this.isUploading = false;
                      // Clear file but keep preselection
                      this.clearFileOnly();

                      // Check if it's a timeout error first
                      if (this.isTimeoutError(error)) {
                        const errorInfo = this.getErrorMessage(error, '');
                        this.statusModalTitle = 'Warning';
                        this.isStatusModalOpen = true;
                        this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
                        return;
                      }

                      // Extract error message and details from backend
                      const backendMessage =
                        error.error?.message ||
                        error.message ||
                        'An error occurred during validation.';

                      // Extract error details array if available
                      const errorDetails = error.error?.errors || [];
                      this.statusModalTitle = 'Warning';
                      this.isStatusModalOpen = true;
                      this.statusModal.showError(
                        backendMessage,
                        'Invalid Data',
                        'error',
                        errorDetails,
                      );
                    },
                  });
              }
            },
            error: (error) => {
              this.isUploading = false;

              // Clear file but keep preselection
              this.clearFileOnly();

              // Check if it's a timeout error and show appropriate message
              const errorInfo = this.getErrorMessage(
                error,
                'Failed to upload file to storage. Please try again.',
              );

              // Show error modal for S3 upload failure
              this.statusModalTitle = 'Warning';
              this.isStatusModalOpen = true;
              this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
            },
          });
        },
        error: (error) => {
          this.isUploading = false;
          // Clear file but keep preselection
          this.clearFileOnly();
          // Check if it's a timeout error and show appropriate message
          const errorInfo = this.getErrorMessage(
            error,
            'Failed to initiate file upload. Please try again.',
          );

          // Show error modal for presigned URL generation failure
          this.statusModalTitle = 'Warning';
          this.isStatusModalOpen = true;
          this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
        },
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  /**
   * Download a B&CS assessment document
   */
  downloadDocument(document: (typeof this.bncsDocuments)[0]): void {
    if (!this.legalEntityId) {
      this.logger.error('Cannot download: No entity ID');
      return;
    }

    this.logger.info(
      `Downloading B&CS document: ${document.name} (ID: ${document.document_file_id})`,
    );

    this.legalEntityService
      .generateDownloadUrl(this.legalEntityId, document.document_file_id)
      .subscribe({
        next: (response) => {
          this.logger.info('Download URL generated successfully');
          // Use the downloadFile helper method to trigger browser download
          this.legalEntityService.downloadFile(response.download_url, response.filename);
        },
        error: (error) => {
          this.logger.error('Error generating download URL:', error);
        },
      });
  }

  /**
   * Format Unix timestamp to DD/MMM/YYYY format for display
   */
  private formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Check if error is a timeout error
   */
  private isTimeoutError(error: unknown): boolean {
    const err = error as {
      name?: string;
      message?: string;
      status?: number;
      statusText?: string;
      error?: { message?: string };
    };
    return (
      err?.name === 'TimeoutError' ||
      (err?.message?.toLowerCase().includes('timeout') ?? false) ||
      err?.status === 504 || // Gateway Timeout
      err?.status === 408 || // Request Timeout
      (err?.error?.message?.toLowerCase().includes('timeout') ?? false) ||
      // Browser timeout: status 0 with Unknown Error
      (err?.status === 0 && err?.statusText === 'Unknown Error')
    );
  }

  /**
   * Get appropriate error message based on error type
   */
  private getErrorMessage(
    error: unknown,
    defaultMessage: string,
  ): { title: string; message: string } {
    if (this.isTimeoutError(error)) {
      return {
        title: 'Request Timeout',
        message:
          'The file took too long to process. Please check your connectivity and/or file size and try again.\n\nIf this error keeps occurring please contact support.',
      };
    }

    // Extract backend message or use default
    const err = error as { error?: { message?: string }; message?: string };
    const message = err.error?.message || err.message || defaultMessage;
    return {
      title: 'Upload Failed',
      message,
    };
  }

  /**
   * Reset the document update modal form
   * This should only be called after successful upload
   */
  private resetModalForm(): void {
    if (this.documentUpdateModal) {
      this.documentUpdateModal.resetForm();
    }
  }
}
